import { keepPreviousData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Building2, Link2 } from 'lucide-react';
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ConfirmationModal } from '@/components/modals/confirmation-modal';
import { useModal } from '@/components/modals/new/use-modal';
import { Button } from '@/components/new-ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/new-ui/command';
import { Dialog, DialogContent, DialogTitle } from '@/components/new-ui/dialog';
import { currentTenantAtom } from '@/constants/currrentTenant';
import {
  useLinkProjectEstate,
  useUnlinkEstateFromProject,
} from '@/features/projects/api/project-estates';
import { getProjects } from '@/features/projects/api/projects';
import { searchAllQueryKey } from '@/features/search/api/search';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 15;

type Props = {
  estateId: string;
  project?: { id: string; name: string | null } | null;
};

/**
 * Links an estate to a project or unlinks it. This replaces the link button of the old
 * dashboard, which was the only place in the app to do this.
 */
export const EstateProjectLinkButton = ({ estateId, project }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentTenant = useAtomValue(currentTenantAtom);
  const unlinkModal = useModal();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [searchString, setSearchString] = useState('');

  // Rows from later pages and search results are not refreshed by the invalidation below, so
  // the button keeps the result of its own last link or unlink until the row data catches up
  const [linkedProject, setLinkedProject] = useState(project ?? null);
  useEffect(() => {
    setLinkedProject(project ?? null);
  }, [project]);

  const isLinked = Boolean(linkedProject?.id);

  const filters = {
    ...(currentTenant?.id ? { tenantId: currentTenant.id } : {}),
    ...(searchString ? { searchString } : {}),
  };
  // GET /projects is paginated, so the picker loads the next page when the list is scrolled to
  // the end. Without it only the newest page of projects could be picked without searching
  const {
    data: projectPages,
    isFetching,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['projects', 'link-picker', filters],
    queryFn: ({ pageParam }) =>
      getProjects({
        query: {
          page: pageParam,
          pageSize: PAGE_SIZE,
          ...(Object.keys(filters).length > 0 ? { filters } : {}),
        },
      }),
    initialPageParam: 1,
    // Only an empty page ends the list: the API dedupes joined rows after its limit, so a page
    // can come back shorter than PAGE_SIZE while older projects still follow
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.length > 0 ? lastPageParam + 1 : undefined,
    // Keeps the current list on screen while a search loads instead of an empty picker
    placeholderData: keepPreviousData,
    enabled: isPickerOpen,
  });
  // OFFSET paging can return a project on two pages, which would duplicate keys and cmdk values
  const projects = useMemo(
    () => [...new Map((projectPages?.pages.flat() ?? []).map((item) => [item.id, item])).values()],
    [projectPages]
  );

  // A failed page is not retried on its own, the user retries it with the button below.
  // isFetching also covers a new search loading behind the previous list
  const canLoadMore = Boolean(hasNextPage) && !isFetching && !isFetchNextPageError;
  const loadMoreObserver = useRef<IntersectionObserver | null>(null);
  // The observer is rebuilt when a page finishes loading, so the next page loads only while the
  // sentinel is still in view, not every remaining page at once
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      loadMoreObserver.current?.disconnect();
      if (!node || !canLoadMore) return;
      loadMoreObserver.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreObserver.current?.disconnect();
          void fetchNextPage();
        }
      });
      loadMoreObserver.current.observe(node);
    },
    [canLoadMore, fetchNextPage]
  );

  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: ['estates'] });
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    queryClient.invalidateQueries({ queryKey: searchAllQueryKey });
  };

  const { mutateAsync: linkProjectEstate, isPending: isLinking } = useLinkProjectEstate({
    mutationConfig: {
      onSuccess: (_data, { projectId }) => {
        const picked = projects.find((item) => item.id === projectId);
        setLinkedProject({ id: projectId, name: picked?.name ?? null });
        toast.success(t('link-project-estate-success'));
        invalidateLists();
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { mutateAsync: unlinkEstateFromProject, isPending: isUnlinking } =
    useUnlinkEstateFromProject({
      mutationConfig: {
        onSuccess: () => {
          setLinkedProject(null);
          toast.success(t('unlink-estate-from-project-success'));
          invalidateLists();
        },
        onError: (err) => {
          toast.error(err.message);
        },
      },
    });

  // The picker closes and the unlink modal dismisses before the request finishes, so the
  // button ignores clicks until then, a second click would send a second request
  const isMutating = isLinking || isUnlinking;

  const onSearchChange = useDebouncedCallback((value: string) => setSearchString(value), 300);

  const handleOpenPickerChange = (open: boolean) => {
    setIsPickerOpen(open);
    if (!open) {
      onSearchChange.cancel();
      setSearchString('');
    }
  };

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isMutating) return;
    if (isLinked) {
      unlinkModal.open();
    } else {
      handleOpenPickerChange(true);
    }
  };

  const handleSelectProject = async (projectId: string) => {
    handleOpenPickerChange(false);
    await linkProjectEstate({ projectId, estateId });
  };

  const label = isLinked ? `${t('unlink')}: ${linkedProject?.name ?? ''}` : t('link');

  return (
    // Dialogs are portaled, but React still bubbles their clicks to the row, which would
    // open the estate
    // biome-ignore lint/a11y/noStaticElementInteractions: only stops click bubbling
    // biome-ignore lint/a11y/useKeyWithClickEvents: only stops click bubbling
    <span className="contents" onClick={(event) => event.stopPropagation()}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'h-7 w-7 hover:text-foreground',
          isLinked ? 'text-primary' : 'text-muted-foreground',
          isMutating && 'cursor-not-allowed opacity-50'
        )}
        title={label}
        aria-label={label}
        // Not `disabled`: a disabled button lets the click through to the table row, which
        // opens the estate
        aria-disabled={isMutating}
        onClick={handleClick}
      >
        <Link2 className="h-4 w-4" />
      </Button>

      <Dialog open={isPickerOpen} onOpenChange={handleOpenPickerChange}>
        <DialogContent className="overflow-hidden p-0">
          <DialogTitle className="px-4 pt-4 text-base">{t('selectProject')}</DialogTitle>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('search-project')}
              onValueChange={(value) => onSearchChange(value)}
            />
            <CommandList className="max-h-80 p-1">
              {!isFetching ? <CommandEmpty>{t('no-projects-found')}</CommandEmpty> : null}
              {projects.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => handleSelectProject(item.id)}
                  className="cursor-pointer gap-3 py-2"
                >
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{item.name}</span>
                    {item.address?.address ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {item.address.address}
                      </span>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
              {isFetchNextPageError ? (
                <div className="flex justify-center py-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void fetchNextPage()}
                  >
                    {t('tryAgain')}
                  </Button>
                </div>
              ) : hasNextPage ? (
                <div ref={loadMoreRef} className="h-px" aria-hidden />
              ) : null}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        modal={unlinkModal}
        title={t('confirmUnlinkTitle')}
        description={t('confirmUnlinkProjectDescription')}
        confirmationText={t('unlink')}
        destructive
        onSubmit={async () => {
          if (linkedProject?.id) {
            await unlinkEstateFromProject({ projectId: linkedProject.id, estateId });
          }
        }}
      />
    </span>
  );
};
